/**
 * Tiny ICU-ish message formatter: `{name}`, `{n, number}`, `{n, plural, one {# coin} other {# coins}}`,
 * `{x, select, a {…} other {…}}`. Components never hard-code strings; they call t(key, vars).
 *
 * One dictionary per locale merges the namespaces game + store + share + presets + push
 * (content/i18n; keys are unique across them). English is bundled — the game strings from
 * ./en.json, the source of truth, plus content/i18n/en — and every other locale is ONE lazy
 * chunk (vite.config groups its namespace files), with a per-key fallback to English.
 * `en-XA` is the pseudo-locale (dev): keys its generated bundle lacks are pseudo-localized here.
 *
 * Feature strings: `src/features/<name>/en.json` holds that feature's English keys, all
 * prefixed `feature.<name>.` (checked by test/i18n.test.ts; the i18n tools merge them into
 * content/i18n/<loc>/game.json, so translations arrive with the locale chunk as usual).
 *
 * Pack strings: a downloaded city pack brings its own names and customers' bios
 * (`i18n/<locale>.json` inside the pack, content/cities/<city>/i18n at build time) so the
 * first-load dictionary stays small. The pack manager registers a loader per city
 * (`addPackStrings`); this module keeps the merged pack dictionaries of English and the active
 * locale next to the base ones and reloads them on every `loadLocale`, before the swap, so a
 * language change never shows a half-translated street. The base dictionary always wins: a pack
 * only ADDS keys (the base's keys are dropped from what a pack brings).
 */
import enStore from '@mp/content/i18n/en/store.json';
import enShare from '@mp/content/i18n/en/share.json';
import enPresets from '@mp/content/i18n/en/presets.json';
import enPush from '@mp/content/i18n/en/push.json';
import enGame from './en.json';

export type Messages = Record<string, string>;
export type Vars = Record<string, string | number | boolean | null | undefined>;

/** SY-02.4 launch languages (endonyms for the picker). */
export const LOCALES: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'en', name: 'English' },
  { id: 'es', name: 'Español' },
  { id: 'pt-BR', name: 'Português (Brasil)' },
  { id: 'fr', name: 'Français' },
  { id: 'de', name: 'Deutsch' },
  { id: 'ja', name: '日本語' },
  { id: 'ko', name: '한국어' },
  { id: 'zh-Hans', name: '简体中文' },
  { id: 'id', name: 'Bahasa Indonesia' },
  { id: 'hi', name: 'हिन्दी' },
];
export const PSEUDO_LOCALE = { id: 'en-XA', name: '[Ƥšéûðö]' };
export const NAMESPACES = ['game', 'store', 'share', 'presets', 'push'] as const;

/** Per-feature English strings, eager (tiny): feature name → its dictionary. */
export const FEATURE_STRINGS: Record<string, Messages> = Object.fromEntries(
  Object.entries(import.meta.glob<Messages>('../features/*/en.json', { eager: true, import: 'default' })).map(([path, dict]) => [path.split('/').slice(-2)[0]!, dict]),
);

/** Problems with feature strings: keys outside `feature.<name>.` or clashing with another source. */
export function featureStringProblems(features: Record<string, Messages> = FEATURE_STRINGS, base: Messages = BASE_EN): string[] {
  const out: string[] = [];
  const seen = new Map<string, string>(Object.keys(base).map((k) => [k, 'game']));
  for (const [name, dict] of Object.entries(features)) {
    for (const k of Object.keys(dict)) {
      if (!k.startsWith(`feature.${name}.`)) out.push(`features/${name}/en.json: "${k}" must start with "feature.${name}."`);
      const other = seen.get(k);
      if (other) out.push(`features/${name}/en.json: "${k}" duplicates ${other}`);
      seen.set(k, `features/${name}`);
    }
  }
  return out;
}

/** English, bundled: the game source wins over content/i18n/en/game.json (the tools sync it from here). */
const BASE_EN: Messages = { ...(enPush as Messages), ...(enPresets as Messages), ...(enShare as Messages), ...(enStore as Messages), ...(enGame as Messages) };
const EN: Messages = Object.assign({}, ...Object.values(FEATURE_STRINGS), BASE_EN) as Messages;

const files = import.meta.glob<Messages>(['../../../../content/i18n/*/{game,store,share,presets,push}.json', '!../../../../content/i18n/en/*.json'], { import: 'default' });

let locale = 'en';
let messages: Messages = EN;
const fallback: Messages = EN;
const listeners = new Set<(loc: string) => void>();

/**
 * One pack's strings for a locale: the parsed `i18n/<locale>.json`, or null when the pack has no
 * file for it (the locale is then "not yet translated" and falls back to the pack's English).
 * May reject (a file the device lost): that counts as no file.
 */
export type PackStringLoader = (locale: string) => Promise<Messages | null>;

interface PackStrings {
  load: PackStringLoader;
  /** the pack's English: the fallback for every locale */
  en: Messages;
  /** the pack's strings in the active locale (same object as `en` while the locale is English) */
  loc: Messages;
}

/** Registered packs in registration order (the first one to define a key keeps it). */
const packSources = new Map<string, PackStrings>();
/** The merged pack dictionaries: English (fallback) and the active locale's. */
let packEnglish: Messages = {};
let packLocale: Messages = {};

export function setLocale(next: string, dict: Messages): void {
  locale = next;
  messages = dict;
  for (const l of listeners) l(next);
}

export function onLocaleChange(fn: (loc: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getLocale(): string {
  return locale;
}

/** Locales that have bundles in content/ (others fall back to English per key). */
export function availableLocales(): string[] {
  const out = new Set<string>(['en']);
  for (const path of Object.keys(files)) out.add(path.split('/').slice(-2)[0]!);
  return [...out];
}

/** A pack file's content as messages: string values only, nothing the base dictionary already has (the base always wins). */
function onlyNew(dict: unknown): Messages {
  const out: Messages = {};
  if (dict && typeof dict === 'object' && !Array.isArray(dict)) for (const [k, v] of Object.entries(dict)) if (typeof v === 'string' && !(k in fallback)) out[k] = v;
  return out;
}

/** One pack's strings for `loc`: its file (none → empty), pseudo-localized for en-XA like the base's missing keys. */
async function packLoad(p: PackStringLoader, loc: string, en: Messages): Promise<Messages> {
  if (loc === 'en') return en;
  if (loc === PSEUDO_LOCALE.id) return Object.fromEntries(Object.entries(en).map(([k, v]) => [k, pseudo(v)]));
  return onlyNew(await p(loc).catch(() => null));
}

/** Merge the registered packs' dictionaries (first registration wins a shared key). */
function mergePacks(pick: (p: PackStrings) => Messages): Messages {
  const out: Messages = {};
  for (const p of packSources.values()) for (const [k, v] of Object.entries(pick(p))) if (!(k in out)) out[k] = v;
  return out;
}

function remergePacks(): void {
  packEnglish = mergePacks((p) => p.en);
  packLocale = mergePacks((p) => p.loc);
}

/**
 * Register (or replace) a city pack's strings. Its English and the active locale's are read now
 * (the pack's files are on the device already) and then read again on every `loadLocale`. Keys the
 * base dictionary has are ignored, so a pack can only add. `id` is the city.
 */
export async function addPackStrings(id: string, load: PackStringLoader): Promise<void> {
  const entry: PackStrings = { load, en: onlyNew(await load('en').catch(() => null)), loc: {} };
  packSources.set(id, entry);
  // the locale may change while the file is read: `loadLocale` reads every registered pack, so once
  // it has finished the entry holds the new locale's strings, and a read for the old one is redone
  for (let asked = ''; asked !== locale && packSources.get(id) === entry; ) {
    asked = locale;
    entry.loc = await packLoad(load, asked, entry.en);
  }
  if (packSources.get(id) === entry) remergePacks();
}

/** Forget a pack's strings (its names fall back to the catalog's English names again). */
export function removePackStrings(id: string): void {
  if (packSources.delete(id)) remergePacks();
}

/** Every registered pack's strings for `loc`, read together (and again for a pack registered meanwhile) so the dictionaries can be swapped at once. */
async function loadPackLocale(loc: string): Promise<Map<PackStrings, Messages>> {
  const done = new Map<PackStrings, Messages>();
  for (let todo = [...packSources.values()]; todo.length; todo = [...packSources.values()].filter((p) => !done.has(p))) {
    for (const [p, strings] of await Promise.all(todo.map(async (p) => [p, await packLoad(p.load, loc, p.en)] as const))) done.set(p, strings);
  }
  return done;
}

/** Every namespace of one locale, merged (they share one lazy chunk). */
async function bundle(loc: string): Promise<Messages> {
  const parts = await Promise.all(NAMESPACES.map((ns) => files[`../../../../content/i18n/${loc}/${ns}.json`]?.().catch(() => ({})) ?? Promise.resolve({})));
  return Object.assign({}, ...parts) as Messages;
}

/** Best supported match for a browser language list ('pt-PT' → 'pt-BR', 'zh-CN' → 'zh-Hans'). */
export function matchLocale(prefs: readonly string[]): string {
  const ids = LOCALES.map((l) => l.id);
  for (const p of prefs) {
    if (ids.includes(p)) return p;
    const base = p.split('-')[0]!.toLowerCase();
    if (base === 'zh') return 'zh-Hans';
    if (base === 'pt') return 'pt-BR';
    const hit = ids.find((id) => id.split('-')[0] === base);
    if (hit) return hit;
  }
  return 'en';
}

/** Switch language: loads the locale's chunk, then swaps dictionaries at once (per-key English fallback). */
export async function loadLocale(loc: string): Promise<void> {
  let dict: Messages;
  if (loc === 'en') dict = EN;
  else {
    const got = await bundle(loc);
    dict = loc === PSEUDO_LOCALE.id ? pseudoFill(got, EN) : got;
  }
  // the packs' strings of the new locale come with it: one swap, nothing half-translated
  for (const [p, strings] of await loadPackLocale(loc)) p.loc = strings;
  remergePacks();
  setLocale(loc, dict);
  if (typeof document !== 'undefined') document.documentElement.lang = loc;
}

export function t(key: string, vars: Vars = {}): string {
  const msg = messages[key] ?? packLocale[key] ?? fallback[key] ?? packEnglish[key];
  if (msg === undefined) return key;
  return format(msg, vars, locale === PSEUDO_LOCALE.id ? 'en' : locale);
}

/** Locale-aware "a, b and c". */
export function list(items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale === PSEUDO_LOCALE.id ? 'en' : locale, { type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

export function has(key: string): boolean {
  return key in messages || key in packLocale || key in fallback || key in packEnglish;
}

/**
 * The active locale has its own text for `key` (no per-key English fallback). A sentence built
 * from several keys (a message and the names in it) renders in one language only when all do.
 */
export function translated(key: string): boolean {
  return key in messages || key in packLocale;
}

// ---------------------------------------------------------------- formatting

function matchBrace(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    if (s[i] === '{') depth++;
    else if (s[i] === '}' && --depth === 0) return i;
  }
  return s.length - 1;
}

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberText(v: unknown, loc: string): string {
  if (typeof v !== 'number') return String(v ?? '');
  let nf = numberFormats.get(loc);
  if (!nf) {
    try {
      nf = new Intl.NumberFormat(loc);
    } catch {
      nf = new Intl.NumberFormat('en');
    }
    numberFormats.set(loc, nf);
  }
  return nf.format(v);
}

/** Locale-aware number for components (Amount, prices). */
export function num(v: number): string {
  return numberText(v, locale === PSEUDO_LOCALE.id ? 'en' : locale);
}

/** Parse `key {msg} key {msg}` option lists. */
function options(body: string): Map<string, string> {
  const out = new Map<string, string>();
  let i = 0;
  while (i < body.length) {
    while (i < body.length && /\s/.test(body[i]!)) i++;
    const start = i;
    while (i < body.length && body[i] !== '{' && !/\s/.test(body[i]!)) i++;
    const key = body.slice(start, i);
    while (i < body.length && body[i] !== '{') i++;
    if (i >= body.length) break;
    const end = matchBrace(body, i);
    out.set(key, body.slice(i + 1, end));
    i = end + 1;
  }
  return out;
}

const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(n: number, loc: string): string {
  let pr = pluralRules.get(loc);
  if (!pr) {
    try {
      pr = new Intl.PluralRules(loc);
    } catch {
      pr = new Intl.PluralRules('en');
    }
    pluralRules.set(loc, pr);
  }
  return pr.select(n);
}

function formatArg(inner: string, vars: Vars, loc: string): string {
  const c1 = inner.indexOf(',');
  const name = (c1 < 0 ? inner : inner.slice(0, c1)).trim();
  const value = vars[name];
  if (c1 < 0) return numberText(value, loc);
  const rest = inner.slice(c1 + 1);
  const c2 = rest.indexOf(',');
  const type = (c2 < 0 ? rest : rest.slice(0, c2)).trim();
  const body = c2 < 0 ? '' : rest.slice(c2 + 1);
  if (type === 'number') return numberText(value, loc);
  if (type === 'plural') {
    const n = Number(value ?? 0);
    const opts = options(body);
    const chosen = opts.get(`=${n}`) ?? opts.get(pluralCategory(n, loc)) ?? opts.get('other') ?? '';
    return format(chosen.replace(/#/g, numberText(n, loc)), vars, loc);
  }
  if (type === 'select') {
    const opts = options(body);
    return format(opts.get(String(value)) ?? opts.get('other') ?? '', vars, loc);
  }
  return numberText(value, loc);
}

export function format(msg: string, vars: Vars, loc = locale): string {
  let out = '';
  for (let i = 0; i < msg.length; i++) {
    const ch = msg[i]!;
    if (ch === '{') {
      const end = matchBrace(msg, i);
      out += formatArg(msg.slice(i + 1, end), vars, loc);
      i = end;
    } else out += ch;
  }
  return out;
}

// ---------------------------------------------------------------- pseudo-locale (dev)

const ACCENTS: Record<string, string> = {
  a: 'á', b: 'ƀ', c: 'ç', d: 'ð', e: 'é', f: 'ƒ', g: 'ĝ', h: 'ĥ', i: 'í', j: 'ĵ', k: 'ķ', l: 'ļ', m: 'ɱ',
  n: 'ñ', o: 'ö', p: 'þ', q: 'ǫ', r: 'ŕ', s: 'š', t: 'ţ', u: 'û', v: 'ṽ', w: 'ŵ', x: 'ẋ', y: 'ý', z: 'ž',
  A: 'Å', B: 'Ɓ', C: 'Ç', D: 'Ð', E: 'É', F: 'Ƒ', G: 'Ĝ', H: 'Ĥ', I: 'Î', J: 'Ĵ', K: 'Ķ', L: 'Ļ', M: 'Ṁ',
  N: 'Ñ', O: 'Ö', P: 'Þ', Q: 'Ǫ', R: 'Ŕ', S: 'Š', T: 'Ţ', U: 'Û', V: 'Ṽ', W: 'Ŵ', X: 'Ẋ', Y: 'Ý', Z: 'Ž',
};
const PROTECTED = /(Market Pop|Pip)/g;

function accent(text: string): string {
  return text
    .split(PROTECTED)
    .map((part, i) => (i % 2 === 1 ? part : part.replace(/[A-Za-z]/g, (c) => ACCENTS[c] ?? c)))
    .join('');
}

/** Accent message text while keeping ICU syntax (argument names, keywords, option keys, #). */
function pseudoText(msg: string): string {
  let out = '';
  for (let i = 0; i < msg.length; i++) {
    const ch = msg[i]!;
    if (ch !== '{') {
      out += ch === '#' ? ch : accent(ch);
      continue;
    }
    const end = matchBrace(msg, i);
    const inner = msg.slice(i + 1, end);
    const c1 = inner.indexOf(',');
    const rest = c1 < 0 ? '' : inner.slice(c1 + 1);
    const c2 = rest.indexOf(',');
    if (c1 < 0 || c2 < 0) out += `{${inner}}`;
    else {
      const head = inner.slice(0, c1 + 1 + c2 + 1);
      const opts = options(rest.slice(c2 + 1));
      out += `{${head}${[...opts].map(([k, v]) => ` ${k} {${pseudoText(v)}}`).join('')}}`;
    }
    i = end;
  }
  return out;
}

/** Runtime pseudo-localization for keys the generated en-XA bundle does not have yet. */
export function pseudo(msg: string): string {
  const width = msg.replace(/\{[^{}]*\}/g, 'xxxx').length;
  return `[${pseudoText(msg)}${'~'.repeat(Math.ceil(width * 0.35))}]`;
}

function pseudoFill(got: Messages, en: Messages): Messages {
  const out: Messages = { ...got };
  for (const [k, v] of Object.entries(en)) if (!(k in out)) out[k] = /^name\.(adjectives|nouns)$/.test(k) ? v : pseudo(v);
  return out;
}
